/* ============================================================
   LUXE NAVIGATION v2 — interaction layer (site-redesign-2026 Phase 3b)
   Frozen ref: design/collections-nav-sample-v4.html footer notes (binding):
     - dropdowns open on click AND hover with ~300ms hover intent,
       stay open until EXPLICIT close (Esc / outside click / another opens),
       full keyboard support
     - hide-on-scroll header only after ~400px down, reveals on the first
       upward scroll, NEVER while the mobile menu / cart drawer / a dropdown
       is open; reduced-motion = instant toggle (CSS side in luxe-nav.css)

   Extends the theme machinery rather than replacing it:
     - desktop: layers on menu-dropdown.js's <dropdown-menu> custom element
       (its open/close/keyboard methods are reused; only its trigger timing
       is intercepted via capture-phase listeners)
     - hide-on-scroll: patches the theme's <sticky-header> hide() with the
       LUXE guardrails — no parallel scroll listener
     - mobile: accordion toggles for the .luxe-mnav drawer (drawer open/close,
       focus trap and body scroll-lock stay with the theme's HeaderDrawer)
   Vanilla, deferred, no dependencies.
   ============================================================ */
(function () {
  'use strict';

  var HOVER_INTENT_MS = 120; // Dan 2026-07-08: 300ms felt laggy - short intent only
  var LEAVE_GRACE_MS = 300;  // Dan 2026-07-08: close on leave (grace covers the gap into the panel)
  var HIDE_AFTER_PX = 400;   // frozen footer note: hide only after ~400px down

  /* ---------------- desktop dropdowns ---------------- */
  function initDesktopDropdowns() {
    var header = document.querySelector('.shopify-section-header');
    if (!header) return;

    var navs = header.querySelectorAll('.header__inline-menu');
    if (!navs.length) return;

    // Only dropdown-menus that carry a LUXE panel get the new behaviour.
    var menus = [];
    navs.forEach(function (nav) {
      nav.querySelectorAll('dropdown-menu').forEach(function (menu) {
        if (menu.querySelector(':scope > .luxe-nd')) menus.push(menu);
      });
    });
    if (!menus.length) return; // nothing LUXE rendered — leave the theme alone

    // Dan overruled the frozen "stay open until explicit close" behaviour
    // (2026-07-08): leaving the menu area now closes the dropdown after a
    // short grace period (enough to traverse into the panel), re-entering
    // cancels it. We still swallow the theme's own capture-phase mouseleave
    // so our grace timer is the only close timing in play.
    var leaveTimer = null;
    navs.forEach(function (nav) {
      var wrapper = nav.querySelector('.list-menu--inline');
      if (!wrapper) return;
      wrapper.addEventListener('mouseleave', function (e) {
        e.stopImmediatePropagation();
        window.clearTimeout(leaveTimer);
        leaveTimer = window.setTimeout(function () { closeAll(); }, LEAVE_GRACE_MS);
      }, true);
      wrapper.addEventListener('mouseenter', function () {
        window.clearTimeout(leaveTimer);
      }, true);
    });

    var suppressFocusOpen = false;

    function clampPanel(menu) {
      // Panel is centred on its item (CSS); nudge it back inside the viewport.
      var panel = menu.querySelector(':scope > .luxe-nd');
      if (!panel) return;
      panel.style.marginInlineStart = '0px';
      var rect = panel.getBoundingClientRect();
      var pad = 16;
      var shift = 0;
      if (rect.left < pad) shift = pad - rect.left;
      else if (rect.right > window.innerWidth - pad) shift = (window.innerWidth - pad) - rect.right;
      if (shift) panel.style.marginInlineStart = shift + 'px';
    }

    function openMenu(menu) {
      if (menu.classList.contains('is-active')) return;
      // Pre-set is-hover so the theme's addClasses() opens without its own
      // extra first-open delay — the 300ms intent already happened.
      header.classList.add('is-hover');
      if (typeof menu.handleMouseover === 'function') menu.handleMouseover();
      clampPanel(menu);
    }

    function closeAll(focusTarget) {
      menus.forEach(function (menu) {
        if (menu.classList.contains('is-active') && typeof menu.close === 'function') menu.close();
      });
      header.classList.remove('is-hover');
      if (focusTarget) {
        // The theme reopens dropdowns on focusin — suppress that one echo.
        suppressFocusOpen = true;
        focusTarget.focus();
        window.setTimeout(function () { suppressFocusOpen = false; }, 100);
      }
    }

    menus.forEach(function (menu) {
      var trigger = menu.querySelector(':scope > a.header__menu-item--top');
      var intentTimer = null;

      // 300ms hover intent — replaces the theme's immediate mouseenter open.
      menu.addEventListener('mouseenter', function (e) {
        e.stopImmediatePropagation();
        if (menu.classList.contains('is-active')) return;
        window.clearTimeout(intentTimer);
        intentTimer = window.setTimeout(function () { openMenu(menu); }, HOVER_INTENT_MS);
      }, true);

      // Leaving before the intent fires cancels it; an OPEN menu stays open.
      menu.addEventListener('mouseleave', function () {
        window.clearTimeout(intentTimer);
      }, true);

      // Click opens when closed; a second click follows the link (the plum
      // "All X" row inside the panel is the primary route to the collection).
      if (trigger) {
        trigger.addEventListener('click', function (e) {
          if (menu.classList.contains('is-active')) return;
          e.preventDefault();
          window.clearTimeout(intentTimer);
          openMenu(menu);
        });
      }

      // Keyboard: the theme's focusin handler opens the menu (kept); we only
      // guard the post-Esc echo and clamp the panel once it is open.
      menu.addEventListener('focusin', function (e) {
        if (suppressFocusOpen) {
          e.stopImmediatePropagation();
          return;
        }
        window.requestAnimationFrame(function () {
          if (menu.classList.contains('is-active')) clampPanel(menu);
        });
      }, true);
    });

    // Esc closes and hands focus back to the open item's top-level link.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      var active = menus.filter(function (m) { return m.classList.contains('is-active'); });
      if (!active.length) return;
      var trigger = active[0].querySelector(':scope > a.header__menu-item--top');
      closeAll(trigger || null);
    });

    // Explicit close: click or focus anywhere outside the header.
    document.addEventListener('click', function (e) {
      if (header.contains(e.target)) return;
      closeAll();
    });
    document.addEventListener('focusin', function (e) {
      if (header.contains(e.target)) return;
      var anyOpen = menus.some(function (m) { return m.classList.contains('is-active'); });
      if (anyOpen) closeAll();
    });

    // Keep an open panel on-screen when the window is resized.
    window.addEventListener('resize', function () {
      menus.forEach(function (menu) {
        if (menu.classList.contains('is-active')) clampPanel(menu);
      });
    });
  }

  /* ---------------- mobile menu accordion ---------------- */
  function initMobileAccordion() {
    document.querySelectorAll('.luxe-mnav').forEach(function (drawer) {
      drawer.addEventListener('click', function (e) {
        var btn = e.target.closest('.luxe-mnav__toggle[aria-expanded]');
        if (!btn || !drawer.contains(btn)) return;
        var expanded = btn.getAttribute('aria-expanded') === 'true';

        // Accordion (not drill-down): opening one folds the others.
        drawer.querySelectorAll('.luxe-mnav__toggle[aria-expanded="true"]').forEach(function (other) {
          if (other === btn) return;
          other.setAttribute('aria-expanded', 'false');
          var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
          if (otherPanel) otherPanel.hidden = true;
        });

        btn.setAttribute('aria-expanded', String(!expanded));
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = expanded;
      });
    });
  }

  /* ---------------- hide-on-scroll guardrails ---------------- */
  function overlayOpen() {
    var b = document.body.classList;
    if (b.contains('menu-mobile--open') || b.contains('menu-mobile--opening')) return true; // mobile menu
    if (b.contains('mini-cart--open') || b.contains('mini-cart--opening')) return true;     // cart drawer
    // an open desktop dropdown / mega menu also pins the header
    return !!document.querySelector('.shopify-section-header dropdown-menu.is-active, .shopify-section-header mega-menu.is-active');
  }

  function initHideOnScroll() {
    var sticky = document.querySelector('sticky-header[data-luxe-hide-scroll]');
    if (!sticky || !window.customElements) return;

    window.customElements.whenDefined('sticky-header').then(function () {
      var proto = Object.getPrototypeOf(sticky);
      if (typeof proto.hide !== 'function') return;

      // data-scroll-up is set by header.liquid; belt-and-braces in case the
      // theme's IntersectionObserver read raced this script.
      sticky.scrollUpActive = true;

      // Instance method shadows the prototype: the theme's own scroll loop
      // calls it, so no parallel scroll listener is added.
      sticky.hide = function () {
        var y = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (y < HIDE_AFTER_PX) return; // never within the first ~400px
        if (overlayOpen()) return;     // never while menu / drawer / dropdown open
        proto.hide.call(this);
      };
      // reveal() stays untouched: the theme already reveals on the first
      // upward scroll; reduced-motion instant toggle handled in luxe-nav.css.
    });
  }

  /* ---------------- shared header-offset contract ----------------
     Publishes --luxe-header-offset on <html>: the px height of the header
     currently occupying the top of the viewport, 0 when it is hidden or
     scrolled away. Consumed by components that pin below the header
     (e.g. the Help Me Choose anchor-chip row). No direct coupling. */
  function initHeaderOffsetVar() {
    var headerSection = document.querySelector('.shopify-section-header');
    if (!headerSection) return;

    var lastValue = null;
    function setVar() {
      var offset;
      if (headerSection.classList.contains('shopify-section-header-hidden')) {
        offset = 0; // hidden by hide-on-scroll
      } else if (headerSection.classList.contains('shopify-section-header-sticky')) {
        offset = headerSection.getBoundingClientRect().height; // pinned
      } else {
        // static header: whatever part of it still sits in the viewport
        offset = Math.max(0, headerSection.getBoundingClientRect().bottom);
      }
      offset = Math.round(offset);
      if (offset === lastValue) return;
      lastValue = offset;
      document.documentElement.style.setProperty('--luxe-header-offset', offset + 'px');
    }

    setVar();
    window.addEventListener('resize', setVar);
    // show/hide flips are class changes on the section wrapper
    new MutationObserver(setVar).observe(headerSection, { attributes: true, attributeFilter: ['class'] });
    // keep the static (non-sticky) case honest without heavy work
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        setVar();
      });
    }, { passive: true });
  }

  function init() {
    initDesktopDropdowns();
    initMobileAccordion();
    initHideOnScroll();
    initHeaderOffsetVar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
